import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Channel as StreamChannel } from 'stream-chat'
import { useChatContext, useTranslationContext } from 'stream-chat-react'
import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet'
import { Capacitor } from '@capacitor/core'
import { t } from 'ttag'
import { CompanyId, GoalId, Role } from '../../services/api.types'
import { store } from '../../index'
import YumButton from '../YumButton/YumButton'
import { ReactComponent as MoreIcon } from '../../assets/v3/icons/more.svg'
import { hasRoleInCompany } from '../../common/auth/authSelectors'
import { hasCompanyFeatureFlag } from '../../common/helpers/featureFlags'
import { getLoadingChatImagesExport } from '../../common/loading/loadingSelectors'
import { userCompaniesSelector } from '../../views/Settings/store/SettingsSelectors'
import { IGlobalState } from '../../Root/store/appReducer'
import { getChatImagesExport, getChatMessagesExport } from '../../views/Activity/store/ActivityActions'

export interface IYumChatMoreMenuProps {
  channel: StreamChannel
  // Present only for challenge channels — enables the admin export actions.
  goalId?: GoalId
  companyId?: CompanyId
}

// The "..." menu with the same actions as the challenge chat view's header: mute/unmute for
// everyone, plus the admin export actions for challenge channels. Native platforms get the
// system action sheet, web gets the dropdown.
export default function YumChatMoreMenu(props: IYumChatMoreMenuProps) {
  const { channel, goalId, companyId } = props

  const { client } = useChatContext('YumChatMoreMenu')
  // Stream Chat's own translation function (chat-native strings like Mute/Unmute).
  // App-specific strings use ttag's `t` instead so they get extracted into translations.pot.
  const { t: chatT } = useTranslationContext('YumChatMoreMenu')

  const [muted, setMuted] = useState(false)
  const [contextMenuVisible, setContextMenuVisible] = useState(false)
  const isExportingImages = useSelector(getLoadingChatImagesExport)

  useEffect(() => {
    setMuted(channel.muteStatus().muted)
  }, [channel])

  const state = store.getState() as any as IGlobalState
  const isAdmin = !!goalId && !!companyId && hasRoleInCompany(state, companyId, Role.ADMIN)
  const showImagesExport =
    isAdmin && !!companyId && hasCompanyFeatureFlag(userCompaniesSelector(state), companyId, 'chatImagesExport')

  const toggleMute = async () => {
    setContextMenuVisible(false)
    if (muted) {
      await channel.unmute({ user_id: client.userID })
      setMuted(false)
    } else {
      await channel.mute({ user_id: client.userID })
      setMuted(true)
    }
  }

  const exportChatMessages = async () => {
    setContextMenuVisible(false)
    store.dispatch(getChatMessagesExport(goalId))
  }

  const exportChatImages = async () => {
    setContextMenuVisible(false)
    // Guard against duplicate exports while one is already running.
    if (getLoadingChatImagesExport(store.getState() as any as IGlobalState)) {
      return
    }
    store.dispatch(getChatImagesExport(goalId))
  }

  const toggleContextMenu = () => {
    if (Capacitor.getPlatform() === 'web') {
      return setContextMenuVisible(!contextMenuVisible)
    }

    try {
      const showActions = async () => {
        const options = []
        const actions: Array<() => Promise<void>> = []

        if (muted) {
          options.push({ title: chatT('Unmute'), style: ActionSheetButtonStyle.Default })
        } else {
          options.push({ title: chatT('Mute'), style: ActionSheetButtonStyle.Destructive })
        }
        actions.push(toggleMute)

        if (isAdmin) {
          options.push({ title: t`Export messages`, style: ActionSheetButtonStyle.Default })
          actions.push(exportChatMessages)
        }
        if (showImagesExport) {
          options.push({
            title: isExportingImages ? t`Exporting images…` : t`Export images`,
            style: ActionSheetButtonStyle.Default,
          })
          actions.push(exportChatImages)
        }

        options.push({ title: t`Cancel`, style: ActionSheetButtonStyle.Cancel })

        const result = await ActionSheet.showActions({ options })

        if (result && actions[result.index]) {
          await actions[result.index]()
        }
      }

      showActions()
    } catch (e) {
      return setContextMenuVisible(!contextMenuVisible)
    }
  }

  return (
    <>
      <YumButton
        variant="outlined"
        size="small"
        onClick={toggleContextMenu}
        title={t`More`}
        aria-label={t`More`}
        aria-haspopup="menu"
        aria-expanded={contextMenuVisible}
      >
        <MoreIcon style={{ width: '1rem', height: '1rem' }} aria-hidden="true" />
      </YumButton>

      <div className="yum-goal-card-context-menu">
        <ul
          className={`yum-goal-card-context-menu__dropdown ${
            contextMenuVisible ? 'yum-goal-card-context-menu__dropdown--open' : ''
          }`}
          {...(!contextMenuVisible && { inert: '' })}
        >
          <li>
            <button className="yum-goal-card-context-menu__link" type="button" onClick={toggleMute}>
              {muted ? chatT('Unmute') : chatT('Mute')}
            </button>
          </li>
          {isAdmin && (
            <li>
              <button className="yum-goal-card-context-menu__link" type="button" onClick={exportChatMessages}>
                {t`Export messages`}
              </button>
            </li>
          )}
          {showImagesExport && (
            <li>
              <button
                className="yum-goal-card-context-menu__link"
                type="button"
                onClick={exportChatImages}
                disabled={isExportingImages}
              >
                {isExportingImages ? t`Exporting images…` : t`Export images`}
              </button>
            </li>
          )}
        </ul>
        {contextMenuVisible && <div className="yum-goal-card-context-menu__backdrop" onClick={toggleContextMenu} />}
      </div>
    </>
  )
}
